/**
 * Sales Position Detail Page
 * View and edit a sales position, manage territory assignments, view direct reports
 */

import React, { useState, useEffect } from "react";
import { useParams, useNavigate, useSearchParams } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
  getSalesPosition,
  getSalesPositions,
  createSalesPosition,
  updateSalesPosition,
  getRepCode,
  setRepCode as saveRepCode,
  deleteSalesPosition,
  getDirectReports,
  createTerritoryAssignment,
  deleteTerritoryAssignment,
  getAccountReviews,
} from "../services/salesTeam.service";
import { useAuth } from "../contexts/AuthContext";
import { setDirectorAutoAssign } from "../services/promotionRules.service";
import { useToast } from "../components/ui/Toast";
import { searchOrganizations } from "../services/organizations.service";
import { inviteUserViaEdgeFunction } from "../services/auth.service";
import { UserRole } from "../types/auth.types";
import { defaultInviteRole } from "../utils/salesInviteRole";
import Button from "../components/common/Button";
import SchedulingDelegatesCard from "../components/sales/SchedulingDelegatesCard";
import LoadingSpinner from "../components/common/LoadingSpinner";
import Badge from "../components/ui/Badge";
import {
  ArrowLeftIcon,
  PencilIcon,
  TrashIcon,
  PlusIcon,
  UserCircleIcon,
  BuildingOfficeIcon,
  ChevronRightIcon,
  XMarkIcon,
  MagnifyingGlassIcon,
  EnvelopeIcon,
  CalendarDaysIcon,
} from "@heroicons/react/24/outline";
import type { UUID } from "../types/common.types";
import type {
  SalesPositionType,
  CreateSalesPositionInput,
  UpdateSalesPositionInput,
} from "../types/sales.types";
import { POSITION_TYPE_LABELS } from "../types/sales.types";
import { describeDateModelError } from "../services/eventDateModel.service";

/** Mirrors msr_rep_codes_format (mig 2026-10-19-561). */
const REP_CODE_PATTERN = /^[A-Za-z0-9][A-Za-z0-9._-]{0,19}$/;

const SalesPositionDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { user } = useAuth();
  const { showToast } = useToast();
  const isNew = !id || id === "create";
  const parentIdFromQuery = searchParams.get("parentId") || "";

  // Form state
  const [isEditing, setIsEditing] = useState(isNew);
  const [title, setTitle] = useState("");
  const [positionType, setPositionType] =
    useState<SalesPositionType>("sales_rep");
  const [parentPositionId, setParentPositionId] =
    useState<string>(parentIdFromQuery);
  const [assignedName, setAssignedName] = useState("");
  const [assignedEmail, setAssignedEmail] = useState("");
  const [territory, setTerritory] = useState("");
  const [region, setRegion] = useState("");
  const [selectedStates, setSelectedStates] = useState<string[]>([]);
  const [hireDate, setHireDate] = useState("");
  const [positionStartDate, setPositionStartDate] = useState("");
  const [isPartTime, setIsPartTime] = useState(false);
  const [isActive, setIsActive] = useState(true);
  const [notes, setNotes] = useState("");
  // MSR QA notes 2026-10-19, item 11: rep code, set at MSR setup. Stored in msr_rep_codes (mig 561); RLS lets only
  // superadmin / admin / manager write it, so only they see it as editable and only their saves write it.
  const [repCode, setRepCode] = useState("");
  const canEditRepCode =
    user?.role === "superadmin" ||
    user?.role === "admin" ||
    user?.role === "manager" ||
    user?.role === "regional_manager" ||
    user?.role === "territory_manager";

  const [error, setError] = useState<string | null>(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [showAssignOrg, setShowAssignOrg] = useState(false);
  const [orgSearchQuery, setOrgSearchQuery] = useState("");
  const [orgSearchResults, setOrgSearchResults] = useState<
    Array<{
      id: string;
      name: string;
      address_city?: string;
      address_state?: string;
    }>
  >([]);
  const [orgSearchLoading, setOrgSearchLoading] = useState(false);

  // Invite user state
  const [showInviteModal, setShowInviteModal] = useState(false);
  const [inviteRole, setInviteRole] = useState<UserRole>(UserRole.STAFF);
  const [invitePending, setInvitePending] = useState(false);
  const [inviteSuccess, setInviteSuccess] = useState(false);
  const [inviteError, setInviteError] = useState<string | null>(null);

  // Fetch position data
  const { data: position, isLoading } = useQuery({
    queryKey: ["sales-position", id],
    queryFn: () => getSalesPosition(id!),
    enabled: !isNew && !!id,
  });

  // Fetch all positions for parent dropdown
  const { data: savedRepCode } = useQuery({
    queryKey: ["msr-rep-code", id],
    queryFn: () => getRepCode(id as UUID),
    enabled: !!id && !isNew,
  });
  useEffect(() => {
    setRepCode(savedRepCode ?? "");
  }, [savedRepCode]);

  const { data: allPositions } = useQuery({
    queryKey: ["sales-positions-all"],
    queryFn: () => getSalesPositions({ is_active: true }),
  });

  // Fetch direct reports
  const { data: directReports } = useQuery({
    queryKey: ["sales-direct-reports", id],
    queryFn: () => getDirectReports(id!),
    enabled: !isNew && !!id,
  });

  // Fetch account reviews for this position
  const { data: positionReviews } = useQuery({
    queryKey: ["account-reviews", { sales_position_id: id }],
    queryFn: () => getAccountReviews({ submitted_by: undefined }),
    enabled: !isNew && !!id,
    select: (data) => data.filter((r) => r.sales_position_id === id),
  });

  // When navigating to create mode, ensure editing is enabled and form is reset
  useEffect(() => {
    if (isNew) {
      setIsEditing(true);
      setTitle("");
      setPositionType("sales_rep");
      setParentPositionId(parentIdFromQuery);
      setAssignedName("");
      setAssignedEmail("");
      setTerritory("");
      setRegion("");
      setSelectedStates([]);
      setHireDate("");
      setPositionStartDate("");
      setIsPartTime(false);
      setIsActive(true);
      setNotes("");
      setError(null);
    }
  }, [isNew, parentIdFromQuery]);

  // Populate form from fetched data
  useEffect(() => {
    if (position) {
      setTitle(position.title);
      setPositionType(position.position_type);
      setParentPositionId(position.parent_position_id || "");
      setAssignedName(position.assigned_name || "");
      setAssignedEmail(position.assigned_email || "");
      setTerritory(position.territory || "");
      setRegion(position.region || "");
      setSelectedStates(position.states || []);
      setHireDate(position.hire_date || "");
      setPositionStartDate(position.position_start_date || "");
      setIsPartTime(position.is_part_time);
      setIsActive(position.is_active);
      setNotes(position.notes || "");
    }
  }, [position]);

  // Filter parent options: exclude self and descendants
  const parentOptions = (allPositions || []).filter(
    (p) => p.id !== id && p.position_type !== "sales_rep",
  );

  // Save mutation
  const saveMutation = useMutation({
    mutationFn: async () => {
      const input: CreateSalesPositionInput | UpdateSalesPositionInput = {
        title,
        position_type: positionType,
        parent_position_id: parentPositionId || undefined,
        assigned_name: assignedName || undefined,
        assigned_email: assignedEmail || undefined,
        territory: territory || undefined,
        region: region || undefined,
        states: selectedStates.length > 0 ? selectedStates : undefined,
        hire_date: hireDate || undefined,
        position_start_date: positionStartDate || undefined,
        is_part_time: isPartTime,
        is_active: isActive,
        notes: notes || undefined,
      };

      const saved = isNew
        ? await createSalesPosition(input as CreateSalesPositionInput, user?.id)
        : await updateSalesPosition(
            id!,
            input as UpdateSalesPositionInput,
            user?.id,
          );
      // Rep code is its own row (written after the position exists). A refusal here does not undo the position.
      if (
        canEditRepCode &&
        positionType === "sales_rep" &&
        repCode.trim() !== (savedRepCode ?? "")
      ) {
        try {
          await saveRepCode(saved.id, repCode.trim() === "" ? null : repCode);
        } catch (e) {
          const err = e as { code?: string; message?: string };
          showToast(
            "error",
            err.code === "23505"
              ? "Saved, but that rep code is already used by another rep. Choose another."
              : `Saved, but the rep code wasn't: ${err.message ?? "unknown error"}`,
          );
        }
      }
      return saved;
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ["sales-position"] });
      queryClient.invalidateQueries({ queryKey: ["sales-positions"] });
      queryClient.invalidateQueries({ queryKey: ["sales-positions-all"] });
      queryClient.invalidateQueries({ queryKey: ["sales-direct-reports"] });
      queryClient.invalidateQueries({ queryKey: ["msr-rep-code"] });
      if (isNew) {
        navigate(`/sales-team/${data.id}`, { replace: true });
      } else {
        setIsEditing(false);
      }
    },
    onError: (err: unknown) => {
      // Migration 504: deactivating or moving a position whose rep has a started, unclosed event day is refused with
      // a sentence naming the rep, the day and the event; show that (never the raw database code).
      const e = err as { code?: string; message?: string } | null;
      setError(
        describeDateModelError(err) ??
          e?.message?.replace(/^(TERRITORY|FIELD_MANAGER)_[A-Z_]+:\s*/, "") ??
          "The position could not be saved.",
      );
    },
  });

  // Delete mutation
  const deleteMutation = useMutation({
    mutationFn: () => deleteSalesPosition(id!),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["sales-positions"] });
      navigate("/sales-team", { replace: true });
    },
    onError: (err: unknown) => {
      // Migration 416: a position on an event's Sales Reps list is refused with a sentence naming the events.
      // Close the confirm dialog and show that sentence (never the raw database code).
      setShowDeleteConfirm(false);
      setError(
        describeDateModelError(err) ??
          (err as { message?: string } | null)?.message ??
          "The position could not be deleted.",
      );
      window.scrollTo({ top: 0, behavior: "smooth" });
    },
  });

  // Delete territory assignment
  const deleteAssignment = useMutation({
    mutationFn: (assignmentId: UUID) => deleteTerritoryAssignment(assignmentId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["sales-position", id] });
    },
  });

  // Create territory assignment
  const assignOrgMutation = useMutation({
    mutationFn: (organizationId: UUID) =>
      createTerritoryAssignment(
        { sales_position_id: id!, organization_id: organizationId },
        user?.id,
      ),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["sales-position", id] });
      setShowAssignOrg(false);
      setOrgSearchQuery("");
      setOrgSearchResults([]);
    },
  });

  // Promotion auto-assign toggle (directors only). Server authorizes the caller
  // and re-runs the engine over this director's events; returns the count touched.
  const autoAssignMutation = useMutation({
    mutationFn: (enabled: boolean) => setDirectorAutoAssign(id!, enabled),
    onSuccess: (count, enabled) => {
      queryClient.invalidateQueries({ queryKey: ["sales-position", id] });
      showToast(
        "success",
        `Promotion auto-assign ${enabled ? "enabled" : "disabled"} — ${count} event${count === 1 ? "" : "s"} reconciled`,
      );
    },
    onError: (err: Error) => {
      showToast("error", err.message || "Failed to update auto-assign");
    },
  });

  // Org search with debounce
  useEffect(() => {
    if (!orgSearchQuery.trim() || orgSearchQuery.length < 2) {
      setOrgSearchResults([]);
      return;
    }
    setOrgSearchLoading(true);
    const t = setTimeout(async () => {
      try {
        const results = await searchOrganizations(orgSearchQuery);
        setOrgSearchResults(
          results.map((o) => ({
            id: o.id,
            name: o.name,
            address_city: o.address_city ?? undefined,
            address_state: o.address_state ?? undefined,
          })),
        );
      } catch {
        setOrgSearchResults([]);
      } finally {
        setOrgSearchLoading(false);
      }
    }, 300);
    return () => clearTimeout(t);
  }, [orgSearchQuery]);

  const handleSave = async () => {
    if (!title.trim()) {
      setError("Title is required");
      return;
    }
    // Item 11: rep code -- required when an admin/manager sets up a NEW MSR; same rule as the database check.
    if (canEditRepCode && positionType === "sales_rep") {
      const code = repCode.trim();
      if (isNew && code === "") {
        setError("Rep code is required when setting up an MSR.");
        return;
      }
      if (code !== "" && !REP_CODE_PATTERN.test(code)) {
        setError(
          "Rep code: up to 20 letters or digits; . _ - allowed after the first character, no spaces.",
        );
        return;
      }
    }
    // Check for duplicate email
    if (assignedEmail.trim()) {
      const existing = (allPositions || []).find(
        (p) =>
          p.assigned_email?.toLowerCase() ===
            assignedEmail.trim().toLowerCase() && p.id !== id,
      );
      if (existing) {
        setError(`A sales team member already exists with this email.`);
        return;
      }
    }
    setError(null);
    saveMutation.mutate();
  };

  // Determine if position holder can be invited as CRM user
  const canInvite =
    !isNew &&
    !isEditing &&
    position?.assigned_email &&
    !position?.assigned_profile_id &&
    (user?.role === "superadmin" || user?.role === "admin");

  const handleInvite = async () => {
    if (!position?.assigned_email || !position?.assigned_name) {
      return;
    }
    setInvitePending(true);
    setInviteError(null);
    try {
      const result = await inviteUserViaEdgeFunction(
        position.assigned_email,
        inviteRole,
        undefined,
        undefined,
        position.assigned_name,
      );
      if (result.success && result.user?.id) {
        // Link the new profile to this sales position
        await updateSalesPosition(id!, {
          assigned_profile_id: result.user.id,
        });
        queryClient.invalidateQueries({ queryKey: ["sales-position", id] });
        queryClient.invalidateQueries({ queryKey: ["sales-positions"] });

        // Warn if account was created but email failed to send
        if (result.email_sent === false) {
          setInviteError(
            `Account created but the invitation email could not be sent${result.email_error ? `: ${result.email_error}` : ""}. You may need to resend the invitation from Settings.`,
          );
          return;
        }
      }
      setInviteSuccess(true);
    } catch (err: any) {
      setInviteError(err.message || "Failed to send invitation");
    } finally {
      setInvitePending(false);
    }
  };

  // position_type is free-text in the DB (dirty/mixed-case values exist) — normalize.
  const isDirector =
    !isNew &&
    String(position?.position_type ?? "")
      .toLowerCase()
      .trim() === "director";
  const autoAssignEnabled = !!position?.auto_assign_promotions_enabled;

  if (!isNew && isLoading) {
    return (
      <div className="flex justify-center py-12">
        <LoadingSpinner />
      </div>
    );
  }

  if (!isNew && !position) {
    return (
      <div className="p-6">
        <div className="bg-red-50 text-red-700 p-4 rounded-lg">
          Position not found.
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6 max-w-4xl">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <button
            onClick={() =>
              isNew && parentIdFromQuery
                ? navigate(`/sales-team/${parentIdFromQuery}`)
                : navigate("/sales-team")
            }
            className="text-gray-400 hover:text-gray-600"
          >
            <ArrowLeftIcon className="h-5 w-5" />
          </button>
          <div>
            <h1 className="text-xl font-bold text-gray-900">
              {isNew ? "New Position" : position?.title}
            </h1>
            {!isNew && position && (
              <p className="text-sm text-gray-500">
                {POSITION_TYPE_LABELS[position.position_type]}
                {position.assigned_name && ` · ${position.assigned_name}`}
              </p>
            )}
          </div>
        </div>

        {!isNew && !isEditing && (
          <div className="flex gap-2">
            <Button variant="secondary" onClick={() => setIsEditing(true)}>
              <PencilIcon className="h-4 w-4 mr-1" />
              Edit
            </Button>
            <Button variant="danger" onClick={() => setShowDeleteConfirm(true)}>
              <TrashIcon className="h-4 w-4 mr-1" />
              Delete
            </Button>
          </div>
        )}
      </div>

      {error && (
        <div className="bg-red-50 text-red-700 p-3 rounded-lg text-sm">
          {error}
        </div>
      )}

      {/* Form / View */}
      <div className="bg-white rounded-lg border border-gray-200 p-6 space-y-6">
        <h2 className="text-lg font-semibold text-gray-900">
          Position Details
        </h2>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {/* Title */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Position Title *
            </label>
            {isEditing ? (
              <input
                type="text"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                placeholder="e.g. Southeast Director"
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              />
            ) : (
              <p className="text-sm text-gray-900">{position?.title}</p>
            )}
          </div>

          {/* Position Type */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Level *
            </label>
            {isEditing ? (
              <select
                value={positionType}
                onChange={(e) => {
                  const newType = e.target.value as SalesPositionType;
                  setPositionType(newType);
                  // Auto-populate title from level if title is empty or matches a previous level label
                  const allLabels = Object.values(POSITION_TYPE_LABELS);
                  if (!title.trim() || allLabels.includes(title)) {
                    setTitle(POSITION_TYPE_LABELS[newType]);
                  }
                  // Clear territory/region for top-level roles
                  if (newType === "vp" || newType === "director") {
                    setTerritory("");
                    setRegion("");
                    setSelectedStates([]);
                  }
                }}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
              >
                <option value="vp">VP of Sales</option>
                <option value="director">Director</option>
                <option value="regional_manager">Regional Manager</option>
                <option value="territory_manager">Territory Manager</option>
                <option value="sales_rep">Sales Rep</option>
              </select>
            ) : (
              <Badge
                color={
                  positionType === "vp"
                    ? "purple"
                    : positionType === "director"
                      ? "blue"
                      : "gray"
                }
              >
                {POSITION_TYPE_LABELS[position?.position_type || "sales_rep"]}
              </Badge>
            )}
          </div>

          {/* Reports To */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Reports To
            </label>
            {isEditing ? (
              <select
                value={parentPositionId}
                onChange={(e) => setParentPositionId(e.target.value)}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
              >
                <option value="">None (Top Level)</option>
                {parentOptions.map((p) => (
                  <option key={p.id} value={p.id}>
                    {p.title} ({POSITION_TYPE_LABELS[p.position_type]})
                    {p.assigned_name ? ` - ${p.assigned_name}` : ""}
                  </option>
                ))}
              </select>
            ) : (
              <p className="text-sm text-gray-900">
                {position?.parent_position_id
                  ? allPositions?.find(
                      (p) => p.id === position.parent_position_id,
                    )?.title || "—"
                  : "None (Top Level)"}
              </p>
            )}
          </div>

          {/* Active / Part-Time */}
          <div className="flex gap-6 items-end">
            {isEditing ? (
              <>
                <label className="flex items-center gap-2 text-sm">
                  <input
                    type="checkbox"
                    checked={isActive}
                    onChange={(e) => setIsActive(e.target.checked)}
                    className="rounded border-gray-300"
                  />
                  Active
                </label>
                <label className="flex items-center gap-2 text-sm">
                  <input
                    type="checkbox"
                    checked={isPartTime}
                    onChange={(e) => setIsPartTime(e.target.checked)}
                    className="rounded border-gray-300"
                  />
                  Part-Time
                </label>
              </>
            ) : (
              <div className="flex gap-2">
                <Badge color={position?.is_active ? "green" : "red"}>
                  {position?.is_active ? "Active" : "Inactive"}
                </Badge>
                {position?.is_part_time && (
                  <Badge color="yellow">Part-Time</Badge>
                )}
              </div>
            )}
          </div>
        </div>

        {/* Assigned Person */}
        <div className="border-t pt-4">
          <h3 className="text-sm font-semibold text-gray-700 mb-3">
            Assigned Person
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Name
              </label>
              {isEditing ? (
                <input
                  type="text"
                  value={assignedName}
                  onChange={(e) => setAssignedName(e.target.value)}
                  placeholder="Full name"
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
                />
              ) : (
                <p className="text-sm text-gray-900">
                  {position?.assigned_profile?.full_name ||
                    position?.assigned_name ||
                    "—"}
                </p>
              )}
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Email
              </label>
              {isEditing ? (
                <input
                  type="email"
                  value={assignedEmail}
                  onChange={(e) => setAssignedEmail(e.target.value)}
                  placeholder="Email address"
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
                />
              ) : (
                <p className="text-sm text-gray-900">
                  {position?.assigned_profile?.email ||
                    position?.assigned_email ||
                    "—"}
                </p>
              )}
            </div>
            {positionType === "sales_rep" && (
              <div>
                <label
                  htmlFor="rep-code"
                  className="block text-sm font-medium text-gray-700 mb-1"
                >
                  Rep code{isNew && canEditRepCode ? " *" : ""}
                </label>
                {isEditing && canEditRepCode ? (
                  <>
                    <input
                      id="rep-code"
                      type="text"
                      value={repCode}
                      maxLength={20}
                      onChange={(e) => setRepCode(e.target.value)}
                      placeholder="e.g. TX-101"
                      className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
                      data-testid="rep-code-input"
                    />
                    <p className="mt-1 text-xs text-gray-500">
                      Up to 20 letters or digits (. _ - allowed). Unique across
                      reps. Not a Zuora code.
                    </p>
                  </>
                ) : (
                  <p
                    className="text-sm text-gray-900"
                    data-testid="rep-code-value"
                  >
                    {savedRepCode || "—"}
                    {isEditing && !canEditRepCode && (
                      <span className="ml-2 text-xs text-gray-500">
                        (an admin or manager sets this)
                      </span>
                    )}
                  </p>
                )}
              </div>
            )}
          </div>

          {/* CRM User status indicator + Invite button */}
          {!isNew && !isEditing && position?.assigned_email && (
            <div className="mt-3 flex items-center gap-3">
              {position.assigned_profile_id ? (
                <Badge color="green" size="sm">
                  CRM User
                </Badge>
              ) : (
                <>
                  <Badge color="yellow" size="sm">
                    Not a CRM User
                  </Badge>
                  {canInvite && (
                    <Button
                      size="sm"
                      variant="secondary"
                      onClick={() => {
                        setInviteRole(
                          defaultInviteRole(position.position_type),
                        );
                        setShowInviteModal(true);
                        setInviteSuccess(false);
                        setInviteError(null);
                      }}
                    >
                      <EnvelopeIcon className="h-4 w-4 mr-1" />
                      Invite as CRM User
                    </Button>
                  )}
                </>
              )}
            </div>
          )}
        </div>

        {/* Territory & Region — hidden for VP and Director levels */}
        {positionType !== "vp" && positionType !== "director" && (
          <div className="border-t pt-4">
            <h3 className="text-sm font-semibold text-gray-700 mb-3">
              Territory & Region
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Territory
                </label>
                {isEditing ? (
                  <input
                    type="text"
                    value={territory}
                    onChange={(e) => setTerritory(e.target.value)}
                    placeholder="e.g. North Texas"
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
                  />
                ) : (
                  <p className="text-sm text-gray-900">
                    {position?.territory || "—"}
                  </p>
                )}
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Region
                </label>
                {isEditing ? (
                  <input
                    type="text"
                    value={region}
                    onChange={(e) => setRegion(e.target.value)}
                    placeholder="e.g. Southeast"
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
                  />
                ) : (
                  <p className="text-sm text-gray-900">
                    {position?.region || "—"}
                  </p>
                )}
              </div>
            </div>

            {/* States: removed (owner 2026-10-30, mig 1278) -- Regional / Territory Manager scope is the AD hierarchy only;
                sales_positions.states is kept, unused, and no longer edited here. */}
          </div>
        )}

        {/* Dates */}
        <div className="border-t pt-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Hire Date
              </label>
              {isEditing ? (
                <input
                  type="date"
                  value={hireDate}
                  onChange={(e) => setHireDate(e.target.value)}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
                />
              ) : (
                <p className="text-sm text-gray-900">
                  {position?.hire_date || "—"}
                </p>
              )}
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Position Start Date
              </label>
              {isEditing ? (
                <input
                  type="date"
                  value={positionStartDate}
                  onChange={(e) => setPositionStartDate(e.target.value)}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
                />
              ) : (
                <p className="text-sm text-gray-900">
                  {position?.position_start_date || "—"}
                </p>
              )}
            </div>
          </div>
        </div>

        {/* Notes */}
        <div className="border-t pt-4">
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Notes
          </label>
          {isEditing ? (
            <textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              rows={3}
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
            />
          ) : (
            <p className="text-sm text-gray-900 whitespace-pre-wrap">
              {position?.notes || "—"}
            </p>
          )}
        </div>

        {/* Save / Cancel buttons */}
        {isEditing && (
          <div className="flex gap-2 border-t pt-4">
            <Button onClick={handleSave} disabled={saveMutation.isPending}>
              {saveMutation.isPending ? "Saving..." : "Save"}
            </Button>
            <Button
              variant="secondary"
              onClick={() => {
                if (isNew) {
                  // Navigate back to parent position or list
                  if (parentIdFromQuery) {
                    navigate(`/sales-team/${parentIdFromQuery}`);
                  } else {
                    navigate("/sales-team");
                  }
                } else {
                  setIsEditing(false);
                  // Reset form to original values
                  if (position) {
                    setTitle(position.title);
                    setPositionType(position.position_type);
                  }
                }
              }}
            >
              Cancel
            </Button>
          </div>
        )}
      </div>

      {/* Promotion Auto-Assign (directors only) */}
      {isDirector && !isEditing && (
        <div className="bg-white rounded-lg border border-gray-200 p-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-1">
            Promotion Auto-Assign
          </h2>
          <p className="text-sm text-gray-500 mb-4">
            When enabled, events tied to this director are automatically given
            the promotion(s) configured for their state in{" "}
            <button
              onClick={() => navigate("/admin/promotion-rules")}
              className="text-blue-600 hover:text-blue-800"
            >
              Promotion Rules
            </button>
            . Manual offers and already-issued vouchers are never changed.
          </p>
          <div className="flex items-center gap-3">
            <Badge color={autoAssignEnabled ? "green" : "gray"}>
              {autoAssignEnabled ? "Enabled" : "Disabled"}
            </Badge>
            <Button
              variant="secondary"
              onClick={() => autoAssignMutation.mutate(!autoAssignEnabled)}
              disabled={autoAssignMutation.isPending}
            >
              {autoAssignMutation.isPending
                ? "Updating…"
                : autoAssignEnabled
                  ? "Disable"
                  : "Enable"}
            </Button>
          </div>
        </div>
      )}

      {/* Scheduling delegate (mig 1780): admins name / remove; everyone with the page sees who it is */}
      {!isNew && id && !isEditing && (
        <SchedulingDelegatesCard
          positionId={id}
          positionLabel={
            position?.assigned_profile?.full_name ||
            position?.assigned_name ||
            position?.title ||
            "this position"
          }
          isAdmin={user?.role === "superadmin" || user?.role === "admin"}
        />
      )}

      {/* Direct Reports section */}
      {!isNew && (
        <div className="bg-white rounded-lg border border-gray-200 p-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-semibold text-gray-900">
              Direct Reports ({directReports?.length || 0})
            </h2>
            <Button
              size="sm"
              onClick={() => navigate(`/sales-team/create?parentId=${id}`)}
            >
              <PlusIcon className="h-4 w-4 mr-1" />
              Add Report
            </Button>
          </div>

          {(directReports || []).length === 0 ? (
            <p className="text-sm text-gray-500 text-center py-6">
              No direct reports
            </p>
          ) : (
            <div className="space-y-2">
              {(directReports || []).map((report) => (
                <div
                  key={report.id}
                  className="flex items-center justify-between py-2 px-3 hover:bg-gray-50 rounded-lg cursor-pointer"
                  onClick={() => navigate(`/sales-team/${report.id}`)}
                >
                  <div className="flex items-center gap-3">
                    <UserCircleIcon className="h-8 w-8 text-gray-300" />
                    <div>
                      <div className="text-sm font-medium text-gray-900">
                        {report.assigned_profile?.full_name ||
                          report.assigned_name ||
                          "Unassigned"}
                      </div>
                      <div className="text-xs text-gray-500">
                        {report.title}
                      </div>
                    </div>
                  </div>
                  <ChevronRightIcon className="h-4 w-4 text-gray-400" />
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Territory Assignments */}
      {!isNew && (
        <div className="bg-white rounded-lg border border-gray-200 p-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-semibold text-gray-900">
              Territory Assignments (
              {position?.territory_assignments?.length || 0})
            </h2>
            <Button size="sm" onClick={() => setShowAssignOrg(true)}>
              <PlusIcon className="h-4 w-4 mr-1" />
              Assign Organization
            </Button>
          </div>

          {(position?.territory_assignments || []).length === 0 ? (
            <p className="text-sm text-gray-500 text-center py-6">
              No organizations assigned to this territory
            </p>
          ) : (
            <div className="space-y-2">
              {(position?.territory_assignments || []).map((assignment) => (
                <div
                  key={assignment.id}
                  className="flex items-center justify-between py-2 px-3 hover:bg-gray-50 rounded-lg"
                >
                  <div className="flex items-center gap-3">
                    <BuildingOfficeIcon className="h-6 w-6 text-gray-400" />
                    <div>
                      <div className="text-sm font-medium text-gray-900">
                        {assignment.organization?.name || "Unknown"}
                      </div>
                      <div className="text-xs text-gray-500">
                        {[
                          assignment.organization?.address_city,
                          assignment.organization?.address_state,
                        ]
                          .filter(Boolean)
                          .join(", ") || "—"}
                        {assignment.is_primary && (
                          <Badge color="blue" size="sm" className="ml-2">
                            Primary
                          </Badge>
                        )}
                      </div>
                    </div>
                  </div>
                  <button
                    onClick={() => deleteAssignment.mutate(assignment.id)}
                    className="text-gray-400 hover:text-red-600"
                    title="Remove assignment"
                  >
                    <XMarkIcon className="h-4 w-4" />
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Account Reviews */}
      {!isNew && (
        <div className="bg-white rounded-lg border border-gray-200 p-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-semibold text-gray-900">
              Account Reviews ({positionReviews?.length || 0})
            </h2>
            <Button
              size="sm"
              onClick={() =>
                navigate(`/account-reviews/create?positionId=${id}`)
              }
            >
              <PlusIcon className="h-4 w-4 mr-1" />
              New Review
            </Button>
          </div>

          {(positionReviews || []).length === 0 ? (
            <p className="text-sm text-gray-500 text-center py-6">
              No account reviews submitted from this position
            </p>
          ) : (
            <div className="space-y-2">
              {(positionReviews || []).map((review) => (
                <div
                  key={review.id}
                  className="flex items-center justify-between py-2 px-3 hover:bg-gray-50 rounded-lg cursor-pointer"
                  onClick={() => navigate(`/account-reviews/${review.id}`)}
                >
                  <div>
                    <div className="text-sm font-medium text-gray-900">
                      {review.organization?.name || "Unknown Org"}
                    </div>
                    <div className="text-xs text-gray-500">
                      {new Date(review.review_date).toLocaleDateString()}
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <Badge
                      color={
                        review.status === "reviewed"
                          ? "green"
                          : review.status === "submitted"
                            ? "blue"
                            : "gray"
                      }
                      size="sm"
                    >
                      {review.status === "reviewed"
                        ? "Reviewed"
                        : review.status === "submitted"
                          ? "Submitted"
                          : "Draft"}
                    </Badge>
                    <ChevronRightIcon className="h-4 w-4 text-gray-400" />
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Calendar & Availability */}
      {!isNew && (
        <div className="bg-white rounded-lg border border-gray-200 p-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
              <CalendarDaysIcon className="h-5 w-5 text-gray-400" />
              Calendar & Availability
            </h2>
            <Button size="sm" onClick={() => navigate(`/sales-calendar`)}>
              View Calendar
            </Button>
          </div>
          <p className="text-sm text-gray-500">
            View this rep's events, availability, and calendar requests on the{" "}
            <button
              onClick={() => navigate(`/sales-calendar`)}
              className="text-blue-600 hover:text-blue-800"
            >
              Sales Calendar
            </button>
            .
          </p>
        </div>
      )}

      {/* Delete Confirmation */}
      {showDeleteConfirm && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-sm mx-4">
            <h3 className="text-lg font-semibold mb-2">Delete Position?</h3>
            <p className="text-sm text-gray-600 mb-4">
              This will permanently delete this position. Direct reports will
              need to be reassigned.
            </p>
            <div className="flex gap-2 justify-end">
              <Button
                variant="secondary"
                onClick={() => setShowDeleteConfirm(false)}
              >
                Cancel
              </Button>
              <Button
                variant="danger"
                onClick={() => deleteMutation.mutate()}
                disabled={deleteMutation.isPending}
              >
                {deleteMutation.isPending ? "Deleting..." : "Delete"}
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Invite as CRM User Modal */}
      {showInviteModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-sm mx-4 w-full">
            <h3 className="text-lg font-semibold mb-2">Invite as CRM User</h3>
            {inviteSuccess ? (
              <>
                <div className="bg-green-50 text-green-700 p-3 rounded-lg text-sm mb-4">
                  Invitation sent to {position?.assigned_email}. They will
                  receive an email with a verification code to set up their
                  account.
                </div>
                <div className="flex justify-end">
                  <Button
                    variant="secondary"
                    onClick={() => setShowInviteModal(false)}
                  >
                    Close
                  </Button>
                </div>
              </>
            ) : (
              <>
                <p className="text-sm text-gray-600 mb-4">
                  Send a CRM invitation to{" "}
                  <strong>{position?.assigned_name}</strong> (
                  {position?.assigned_email}). Their new account is linked to
                  this position.
                </p>

                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    CRM Role
                  </label>
                  <select
                    value={inviteRole}
                    onChange={(e) => setInviteRole(e.target.value as UserRole)}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
                  >
                    <option value={UserRole.MSR}>
                      MSR (Membership Sales Rep)
                    </option>
                    <option value={UserRole.STAFF}>Staff</option>
                    <option value={UserRole.MANAGER}>Manager</option>
                    <option value={UserRole.ADMIN}>Admin</option>
                  </select>
                  <p className="text-xs text-gray-500 mt-1">
                    MSR is recommended for sales reps: they get the MSR portal
                    with the events this position is scheduled on. Manager is
                    recommended for territory and regional managers.
                  </p>
                </div>

                {inviteError && (
                  <div className="bg-red-50 text-red-700 p-3 rounded-lg text-sm mb-4">
                    {inviteError}
                  </div>
                )}

                <div className="flex gap-2 justify-end">
                  <Button
                    variant="secondary"
                    onClick={() => setShowInviteModal(false)}
                    disabled={invitePending}
                  >
                    Cancel
                  </Button>
                  <Button onClick={handleInvite} disabled={invitePending}>
                    {invitePending ? "Sending..." : "Send Invitation"}
                  </Button>
                </div>
              </>
            )}
          </div>
        </div>
      )}

      {/* Assign Organization Modal */}
      {showAssignOrg && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-md mx-4 w-full">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold">Assign Organization</h3>
              <button
                onClick={() => {
                  setShowAssignOrg(false);
                  setOrgSearchQuery("");
                  setOrgSearchResults([]);
                }}
                className="text-gray-400 hover:text-gray-600"
              >
                <XMarkIcon className="h-5 w-5" />
              </button>
            </div>

            <div className="relative mb-3">
              <MagnifyingGlassIcon className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
              <input
                type="text"
                value={orgSearchQuery}
                onChange={(e) => setOrgSearchQuery(e.target.value)}
                placeholder="Search organizations by name..."
                className="w-full pl-9 pr-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                autoFocus
              />
            </div>

            <div className="max-h-64 overflow-y-auto">
              {orgSearchLoading && (
                <div className="flex justify-center py-4">
                  <LoadingSpinner />
                </div>
              )}
              {!orgSearchLoading &&
                orgSearchQuery.length >= 2 &&
                orgSearchResults.length === 0 && (
                  <p className="text-sm text-gray-500 text-center py-4">
                    No organizations found
                  </p>
                )}
              {!orgSearchLoading && orgSearchQuery.length < 2 && (
                <p className="text-sm text-gray-500 text-center py-4">
                  Type at least 2 characters to search
                </p>
              )}
              {orgSearchResults.map((org) => {
                const alreadyAssigned = (
                  position?.territory_assignments || []
                ).some((a) => a.organization_id === org.id);
                return (
                  <div
                    key={org.id}
                    className={`flex items-center justify-between py-2 px-3 rounded-lg ${
                      alreadyAssigned
                        ? "opacity-50"
                        : "hover:bg-gray-50 cursor-pointer"
                    }`}
                    onClick={() => {
                      if (!alreadyAssigned) {
                        assignOrgMutation.mutate(org.id);
                      }
                    }}
                  >
                    <div className="flex items-center gap-3">
                      <BuildingOfficeIcon className="h-5 w-5 text-gray-400" />
                      <div>
                        <div className="text-sm font-medium text-gray-900">
                          {org.name}
                        </div>
                        <div className="text-xs text-gray-500">
                          {[org.address_city, org.address_state]
                            .filter(Boolean)
                            .join(", ") || "—"}
                        </div>
                      </div>
                    </div>
                    {alreadyAssigned && (
                      <Badge color="gray" size="sm">
                        Already assigned
                      </Badge>
                    )}
                  </div>
                );
              })}
            </div>

            <div className="flex justify-end mt-4 pt-3 border-t">
              <Button
                variant="secondary"
                onClick={() => {
                  setShowAssignOrg(false);
                  setOrgSearchQuery("");
                  setOrgSearchResults([]);
                }}
              >
                Close
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SalesPositionDetail;
